import { Box, Flex, InlineAlert, Label, RadioButton, Text } from '@modusign/design-system';

import getFormattedParticipantInfo from '../../lib/getFormattedParticiapntInfo';
import isSignableInPersonSigner from '../../lib/isSignableInPersonSigner';
import { InPersonSigner, InPersonSignerWithSigningStatus } from '../../types';
import ParticipantStatusBadge from '../ParticipantStatusBadge';
import { ResponsiveTableCell } from '../ResponsiveTable';

export interface SignerListCellProps {
  signers: Array<InPersonSignerWithSigningStatus>;
  selectedSigner: InPersonSigner | undefined;
  currentSigningOrder: number;
  onSelectParticipant: (signerId: string) => void;
}

export default function SignerListCell({
  signers,
  selectedSigner,
  onSelectParticipant,
  currentSigningOrder,
}: SignerListCellProps) {
  if (signers.length < 1) {
    return null;
  }
  if (signers.length === 1) {
    const participant = signers[0];
    return (
      <ResponsiveTableCell>
        <Text data-testid="one-signer-cell" mr={2}>
          {getFormattedParticipantInfo(participant.name, participant.contactInfo)}
        </Text>
        <ParticipantStatusBadge
          expiry={participant.signingExpiryDate}
          hasSigned={participant.hasSigned}
          isCurrentOrder={participant.signingOrder === currentSigningOrder}
        />
      </ResponsiveTableCell>
    );
  }

  const canUserChooseSigner =
    signers.filter((s) => isSignableInPersonSigner(s, currentSigningOrder)).length > 1;

  return (
    <ResponsiveTableCell maxHeight="204px" overflow="auto">
      <Box>
        {canUserChooseSigner && (
          <Box mb={6}>
            <InlineAlert variant="warning">서명을 진행하려는 서명자를 선택해 주세요.</InlineAlert>
          </Box>
        )}
        <Flex flexDirection="column" gap={5}>
          {signers.map((p) => {
            const id = `${p.participantId}-radio`;
            const disabled = !isSignableInPersonSigner(p, currentSigningOrder);
            return (
              <Flex alignItems="flex-start" key={p.participantId}>
                <Box mt={1}>
                  <RadioButton
                    checked={p.participantId === selectedSigner?.participantId}
                    disabled={disabled}
                    id={id}
                    onClick={() => onSelectParticipant(p.participantId)}
                  />
                </Box>
                <Label fontSize="md" htmlFor={id} mb={0} ml={3}>
                  <Text
                    mr={2}
                    sx={{
                      cursor: disabled ? 'auto' : 'pointer',
                    }}
                  >
                    {getFormattedParticipantInfo(p.name, p.contactInfo)}
                  </Text>
                  <ParticipantStatusBadge
                    expiry={p.signingExpiryDate}
                    hasSigned={p.hasSigned}
                    isCurrentOrder={p.signingOrder === currentSigningOrder}
                  />
                </Label>
              </Flex>
            );
          })}
        </Flex>
      </Box>
    </ResponsiveTableCell>
  );
}
